/**
 * Ghost Verification Page
 * Manage and review ghost application verifications
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  Chip,
  Link,
  SelectChangeEvent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState, TablePagination } from '../../components/common';
import { Card, CardContent, TableCell, TableHeaderCell, TableRow, TableContainer, SearchBox } from '../../components/ui';
import { GhostVerificationService } from '../../services/ghost/GhostVerificationService';
import { formatDateTime } from '../../utils/dateUtils';
import type {
  GhostApplication,
  GhostVerificationStatus,
  PaginatedGhostApplications,
} from '../../types/ghostVerification';

type StatusFilter = GhostVerificationStatus | 'all';

const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
];

const getStatusColor = (status: GhostVerificationStatus, theme: any): string => {
  switch (status) {
    case 'Pending':
      return theme.palette.warning.main;
    case 'Approved':
      return theme.palette.success.main;
    case 'Rejected':
      return theme.palette.error.main;
    default:
      return theme.palette.text.secondary;
  }
};

const GhostVerificationPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<GhostApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<GhostVerificationStatus>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);

  const fetchApplications = useCallback(async () => {
    try {
      setIsLoading(true);
      const result: PaginatedGhostApplications =
        await GhostVerificationService.getApplications(page, pageSize, {
          status: selectedStatuses.size > 0 ? Array.from(selectedStatuses) : 'all',
          search: debouncedSearchValue || undefined,
        });
      setApplications(result.data);
      setTotalResults(result.total);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      setApplications([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, selectedStatuses, debouncedSearchValue]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleStatusToggle = (status: GhostVerificationStatus) => {
    const newSelected = new Set(selectedStatuses);
    if (newSelected.has(status)) {
      newSelected.delete(status);
    } else {
      newSelected.add(status);
    }
    setSelectedStatuses(newSelected);
    setPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };



  const handleViewApplication = (application: GhostApplication) => {
    navigate(`/ghost-verification/${application.id}`);
  };


  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };


  return (
    <MainLayout>


      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: theme.spacing(3),
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: theme.typography.h4.fontWeight,
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(1),
            }}
          >
            Ghost Application Verification
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.text.secondary }}
          >
            Review and verify Ghost applications, licenses, and documentation.
          </Typography>
        </Box>
      </Box>

      {/* Search Bar */}
      <SearchBox
        placeholder="Search by call sign or email"
        value={searchValue}
        onChange={handleSearch}
        containerSx={{ width: { xs: '100%', md: 500 }, marginBottom: theme.spacing(3) }}
      />

      {/* Status Filters */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: theme.typography.subtitle2.fontWeight,
            marginBottom: theme.spacing(1.5),
            color: theme.palette.text.primary,
            fontSize: theme.typography.subtitle2.fontSize,
          }}
        >
          Filter by Status:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
          {statusFilters.map((filter) => {
            const isSelected = selectedStatuses.has(filter.value as GhostVerificationStatus);
            return (
              <Chip
                key={filter.value}
                label={filter.label}
                onClick={() => handleStatusToggle(filter.value as GhostVerificationStatus)}
                onDelete={isSelected ? (e) => {
                  e.stopPropagation();
                  handleStatusToggle(filter.value as GhostVerificationStatus);
                } : undefined}
                clickable
                sx={{
                  backgroundColor: isSelected
                    ? theme.palette.primary.main
                    : theme.palette.background.paper,
                  color: isSelected
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.primary,
                  fontWeight: isSelected ? 500 : 400,
                  borderRadius: theme.shapes.borderRadius.lg,
                  border: `1px solid ${isSelected
                    ? theme.palette.primary.main
                    : theme.customColors.tableBorder
                    }`,
                  cursor: isSelected ? 'default' : 'pointer',
                  '&:hover': {
                    backgroundColor: isSelected
                      ? theme.palette.primary.dark
                      : theme.customColors.tableRowHover,
                  },
                  '& .MuiChip-deleteIcon': {
                    color: isSelected
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.secondary,
                    '&:hover': {
                      color: isSelected
                        ? theme.palette.primary.contrastText
                        : theme.palette.error.main,
                    },
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Applications Table */}
      <Card>
        <CardContent noPadding>
          {isLoading ? (
            <Box sx={{ padding: theme.spacing(6), display: 'flex', justifyContent: 'center' }}>
              <LoadingSpinner />
            </Box>
          ) : applications.length === 0 ? (
            <Box sx={{ padding: theme.spacing(4) }}>
              <EmptyState
                title="No Applications Found"
                message="There are no applications matching your criteria."
              />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Ghost</TableHeaderCell>
                      <TableHeaderCell>Call Sign</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell>Tier</TableHeaderCell>
                      <TableHeaderCell>Submitted On</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {applications.map((application) => (
                      <TableRow key={application.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {application.applicantName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                              {application.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.primary }}>
                          {application.callSign || application.applicantName || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.primary }}>
                          {application.type || 'Registration'}
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.primary }}>
                          {application.ghostTier}
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.secondary }}>
                          {formatDateTime(application.submittedOn)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={application.verificationStatus}
                            sx={{
                              backgroundColor: getStatusColor(application.verificationStatus, theme),
                              color: theme.palette.common.white,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 24,
                              borderRadius: theme.shapes.borderRadius.lg,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => handleViewApplication(application)}
                              sx={{
                                color: theme.palette.primary.main,
                                cursor: 'pointer',
                                textDecoration: 'none',
                                fontSize: theme.typography.body2.fontSize,
                                fontWeight: theme.typography.body2.fontWeight,
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              View
                            </Link>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={totalResults}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default GhostVerificationPage;

